import React, { useEffect, useState } from 'react';
import { PortalStatusPill } from '../components/PortalStatusPill';
import type { CollaborationInstance } from './collaborationControlPlaneShared';

const parentBindingBoundaryLabel = (
  kind: CollaborationInstance['parentBindings'][number]['isolationBoundaryKind']
): string => kind === 'per_customer_relationship' ? 'per-customer relationship' : 'legacy singleton';

interface CollaborationRuntimeCardProps {
  canManage: boolean;
  tenantId: string;
  instances: CollaborationInstance[];
  savingInstanceId: string;
  revokingParentBindingId: string;
  purgingTenantArtifacts: boolean;
  purgingInstanceArtifactsId: string;
  onSaveInstanceSettings: (
    instance: CollaborationInstance,
    update: Partial<Pick<CollaborationInstance, 'acceptsCollaboration' | 'collaborationOutbound' | 'collaborationInbound' | 'defaultCollaborationReplyToEmail'>>
  ) => void;
  onRevokeParentBinding: (instance: CollaborationInstance, parentBindingId: string) => void;
  onPurgeTenantArtifacts: () => void;
  onPurgeInstanceArtifacts: (instance: CollaborationInstance) => void;
  onOpenConsumption: () => void;
}

const CollaborationRuntimeCard = ({
  canManage,
  tenantId,
  instances,
  savingInstanceId,
  revokingParentBindingId,
  purgingTenantArtifacts,
  purgingInstanceArtifactsId,
  onSaveInstanceSettings,
  onRevokeParentBinding,
  onPurgeTenantArtifacts,
  onPurgeInstanceArtifacts,
  onOpenConsumption
}: CollaborationRuntimeCardProps): JSX.Element => {
  const [replyToByInstance, setReplyToByInstance] = useState<Record<string, string>>({});

  useEffect(() => {
    setReplyToByInstance(
      Object.fromEntries(
        instances.map((instance) => [instance.instanceId, instance.defaultCollaborationReplyToEmail ?? ''])
      )
    );
  }, [instances]);

  return (
    <>
      <section className="card" aria-labelledby="collaboration-installations-heading">
        <h3 id="collaboration-installations-heading">Installation connections</h3>
        <p className="muted">
          Choose which SharePoint installations may send or receive supplier work and
          where replies should go. Changes apply only to the named installation.
        </p>
        {instances.length === 0 && (
          <span className="badge-subtle">No SharePoint installations are connected to this tenant yet.</span>
        )}
        <div className="collaboration-installation-list">
          {instances.map((instance) => {
            const replyTo = replyToByInstance[instance.instanceId] ?? '';
            const storedReplyTo = instance.defaultCollaborationReplyToEmail ?? '';
            return (
              <article key={instance.instanceId} className="collaboration-installation-card">
                <div className="collaboration-section-heading">
                  <div>
                    <h4>{instance.displayName || 'SharePoint installation'}</h4>
                    <a href={instance.siteUrl} target="_blank" rel="noreferrer">{instance.siteUrl}</a>
                  </div>
                  <PortalStatusPill
                    label={instance.acceptsCollaboration ? 'Collaboration enabled' : 'Collaboration off'}
                    tone={instance.acceptsCollaboration ? 'success' : 'neutral'}
                  />
                </div>

                <div className="collaboration-direction-grid">
                  <div>
                    <strong>Send supplier work</strong>
                    <p className="muted">Allow auditors at this installation to send Findings and questionnaires.</p>
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onSaveInstanceSettings(instance, { collaborationOutbound: !instance.collaborationOutbound })}
                      disabled={!canManage || savingInstanceId === instance.instanceId}
                    >
                      {instance.collaborationOutbound ? 'Turn off sending' : 'Turn on sending'}
                    </button>
                  </div>
                  <div>
                    <strong>Receive supplier work</strong>
                    <p className="muted">Allow this installation to hold received work in supplier-private workspaces.</p>
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onSaveInstanceSettings(instance, { collaborationInbound: !instance.collaborationInbound })}
                      disabled={!canManage || savingInstanceId === instance.instanceId}
                    >
                      {instance.collaborationInbound ? 'Turn off receiving' : 'Turn on receiving'}
                    </button>
                  </div>
                </div>

                <label className="collaboration-reply-routing">
                  <span><strong>Default reply-to address</strong></span>
                  <span className="muted collaboration-field-help">
                    Used only when a Supplier Profile or template does not provide a more specific address.
                  </span>
                  <div className="collaboration-inline-save">
                    <input
                      className="input"
                      type="email"
                      placeholder="quality@example.com"
                      value={replyTo}
                      disabled={!canManage || savingInstanceId === instance.instanceId}
                      onChange={(event) => setReplyToByInstance((current) => ({
                        ...current,
                        [instance.instanceId]: event.target.value
                      }))}
                    />
                    <button
                      className="btn"
                      type="button"
                      disabled={
                        !canManage ||
                        savingInstanceId === instance.instanceId ||
                        replyTo.trim() === storedReplyTo
                      }
                      onClick={() => onSaveInstanceSettings(instance, {
                        defaultCollaborationReplyToEmail: replyTo.trim()
                      })}
                    >
                      {savingInstanceId === instance.instanceId ? 'Saving…' : 'Save reply routing'}
                    </button>
                  </div>
                </label>

                <div className="collaboration-card-actions">
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => onSaveInstanceSettings(instance, { acceptsCollaboration: !instance.acceptsCollaboration })}
                    disabled={!canManage || savingInstanceId === instance.instanceId}
                  >
                    {instance.acceptsCollaboration ? 'Turn off this connection' : 'Turn on this connection'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <details className="card collaboration-diagnostics">
        <summary>Advanced diagnostics</summary>
        <p className="muted">
          Technical identifiers and covered-tenant runtime bindings for support and fault finding.
          Ordinary supplier setup does not require these values.
        </p>
        <dl className="collaboration-diagnostic-list">
          <div>
            <dt>Customer tenant ID</dt>
            <dd><code>{tenantId}</code></dd>
          </div>
        </dl>
        {instances.map((instance) => (
          <div key={instance.instanceId} className="collaboration-diagnostic-instance">
            <strong>{instance.displayName || instance.siteUrl}</strong>
            <dl className="collaboration-diagnostic-list">
              <div><dt>Installation ID</dt><dd><code>{instance.instanceId}</code></dd></div>
              <div><dt>Runtime mode</dt><dd>{instance.collaborationMode ?? 'standard external'}</dd></div>
              <div><dt>Runtime state</dt><dd>{instance.isActive ? 'active' : 'inactive'}</dd></div>
            </dl>
            {instance.parentBindings.length === 0 ? (
              <p className="muted">No covered-tenant parent binding is recorded.</p>
            ) : (
              <div className="collaboration-binding-list">
                {instance.parentBindings.map((binding) => (
                  <div key={binding.parentBindingId} className="muted">
                    Parent tenant <code>{binding.parentTenantId}</code>
                    {binding.partnerId ? <> · supplier <code>{binding.partnerId}</code></> : null}
                    <> · {parentBindingBoundaryLabel(binding.isolationBoundaryKind)} · {binding.status}</>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </details>

      <section className="card collaboration-maintenance" aria-labelledby="collaboration-maintenance-heading">
        <h3 id="collaboration-maintenance-heading">Maintenance</h3>
        <p className="muted">
          These exceptional actions can disconnect relationships or permanently remove stored
          evidence binaries. They do not remove the supplier quality history or metadata.
        </p>
        {instances.map((instance) => {
          const activeBindings = instance.parentBindings.filter((binding) => binding.status === 'active');
          return (
            <div key={instance.instanceId} className="collaboration-maintenance-row">
              <div>
                <strong>{instance.displayName || instance.siteUrl}</strong>
                <div className="muted">Use only when retiring a connection or responding to a retention request.</div>
              </div>
              <div className="collaboration-card-actions">
                {activeBindings.map((binding) => (
                  <button
                    key={binding.parentBindingId}
                    className="btn secondary collaboration-destructive"
                    type="button"
                    onClick={() => onRevokeParentBinding(instance, binding.parentBindingId)}
                    disabled={!canManage || revokingParentBindingId === binding.parentBindingId}
                  >
                    {revokingParentBindingId === binding.parentBindingId ? 'Revoking…' : 'Revoke parent connection'}
                  </button>
                ))}
                <button
                  className="btn secondary collaboration-destructive"
                  type="button"
                  onClick={() => onPurgeInstanceArtifacts(instance)}
                  disabled={!canManage || purgingInstanceArtifactsId === instance.instanceId}
                >
                  {purgingInstanceArtifactsId === instance.instanceId ? 'Purging…' : 'Purge installation evidence binaries'}
                </button>
              </div>
            </div>
          );
        })}
        <div className="collaboration-tenant-maintenance">
          <div>
            <strong>Entire customer tenant</strong>
            <div className="muted">Permanently purge every stored supplier-collaboration evidence binary for this tenant.</div>
          </div>
          <button
            className="btn secondary collaboration-destructive"
            type="button"
            onClick={onPurgeTenantArtifacts}
            disabled={!canManage || purgingTenantArtifacts}
          >
            {purgingTenantArtifacts ? 'Purging…' : 'Purge all tenant evidence binaries'}
          </button>
        </div>
      </section>

      <p className="muted collaboration-consumption-link">
        Review any supplier collaboration usage recorded for this plan in{' '}
        <button type="button" className="collaboration-inline-link" onClick={onOpenConsumption}>
          Account consumption
        </button>
        .
      </p>
    </>
  );
};

export default CollaborationRuntimeCard;
